import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import type { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { AccountEntry } from '@sorare/core/src/components/mySorare/TransactionsHistory/UserAccountEntries/AccountEntry';

import type { CardWithdrawal_userAccountEntry } from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    id: 'TransactionsHistoryCardWithdrawal.title',
    defaultMessage: 'Card withdrawal fee',
  },
});

type CardWithdrawal_userAccountEntry_tokenOperation_StarkwareWithdrawal =
  CardWithdrawal_userAccountEntry['tokenOperation'] & {
    __typename: 'StarkwareWithdrawal';
  };

type CardWithdrawal_userAccountEntry_tokenOperation_StarkwareWithdrawal_card =
  NonNullable<
    CardWithdrawal_userAccountEntry_tokenOperation_StarkwareWithdrawal['anyCard']
  >;

type Props = {
  userAccountEntry: CardWithdrawal_userAccountEntry;
  primaryCurrency: Currency;
};

type CardWithdrawal = {
  __typename: 'StarkwareWithdrawal';
  destination: string;
  card: CardWithdrawal_userAccountEntry_tokenOperation_StarkwareWithdrawal_card;
};

const isACardWithdrawal = (item: {
  __typename: string;
  anyCard?: CardWithdrawal_userAccountEntry_tokenOperation_StarkwareWithdrawal_card | null;
}): item is CardWithdrawal => {
  return item?.__typename === 'StarkwareWithdrawal' && !!item.anyCard;
};

export const CardWithdrawal = ({
  userAccountEntry,
  primaryCurrency,
}: Props) => {
  const { tokenOperation } = userAccountEntry;
  const { formatMessage } = useIntl();

  if (
    !tokenOperation ||
    !isACardWithdrawal(tokenOperation) ||
    !tokenOperation.anyCard
  )
    return null;

  const { destination, anyCard } = tokenOperation;

  return (
    <AccountEntry
      userAccountEntry={userAccountEntry}
      title={formatMessage(messages.title)}
      primaryCurrency={primaryCurrency}
    >
      <BodyM>
        <FormattedMessage
          id="TransactionsHistoryCardWithdrawal.detail"
          defaultMessage="{card} withdrawn to {destination}"
          values={{ card: anyCard.name, destination }}
        />
      </BodyM>
    </AccountEntry>
  );
};

CardWithdrawal.fragments = {
  userAccountEntry: gql`
    fragment CardWithdrawal_userAccountEntry on UserAccountEntry {
      id
      tokenOperation {
        ... on StarkwareWithdrawal {
          id
          destination
          anyCard {
            slug
            name
          }
        }
      }
      ...AccountEntry_userAccountEntry
    }
    ${AccountEntry.fragments.userAccountEntry}
  ` as TypedDocumentNode<CardWithdrawal_userAccountEntry>,
};
